import React, { createContext, ReactNode } from 'react'
import { TabManager } from './tab-manager'
import { TabHeadings, TabHeadingConfig } from './tab-headings'
import { TabHeadingsFooter } from './tab-headings-footer'
import { TabContentSections, TabContentConfig } from './tab-content-sections'

interface TabHeadingWidths {
  readonly [key: string]: number
}

export interface TabContextProps<TTabs extends string> {
  readonly previousTabID?: TTabs
  readonly currentTabID: TTabs
  readonly setCurrentTabID: (tabID: TTabs) => void
  readonly transitionToggleFlag: boolean
  readonly updateToggleFlag: (flag: boolean) => void
  readonly tabHeadingWidths: TabHeadingWidths
  readonly updateTabHeadingWidths: (widths: TabHeadingWidths) => void
}

interface TabContextUserInput<TTabs extends string> {
  readonly defaultTabID: TTabs
}

// Exported for testing only
// Use generateTabsComponents for the actual application
export function createTabContext<TTabs extends string>({
  defaultTabID
}: TabContextUserInput<TTabs>) {
  return createContext<TabContextProps<TTabs>>({
    currentTabID: defaultTabID,
    setCurrentTabID: () => false,
    transitionToggleFlag: false,
    updateToggleFlag: () => false,
    tabHeadingWidths: {},
    updateTabHeadingWidths: () => false
  })
}

export function generateTabsComponents<TTabs extends string>({
  defaultTabID
}: {
  readonly defaultTabID: TTabs
}) {
  const TabContext = createTabContext<TTabs>({
    defaultTabID
  })

  return {
    TabsManager: ({ children }: { readonly children: ReactNode }) => (
      <TabManager
        context={TabContext}
        defaultTabID={defaultTabID}
        children={children}
      />
    ),
    TabHeadings: ({
      tabHeadingConfigs,
      renderExtra,
      showBackgroundColor
    }: {
      readonly renderExtra?: ReactNode
      readonly showBackgroundColor?: boolean
      readonly tabHeadingConfigs: Array<TabHeadingConfig<TTabs>>
    }) => (
      <TabHeadings<TTabs>
        context={TabContext}
        tabHeadingConfigs={tabHeadingConfigs}
        renderExtra={renderExtra}
        showBackgroundColor={showBackgroundColor}
      />
    ),
    TabHeadingsFooter: ({
      tabHeadingConfigs,
      showHorizontalRule
    }: {
      readonly tabHeadingConfigs: Array<TabHeadingConfig<TTabs>>
      readonly showHorizontalRule?: boolean
    }) => (
      <TabHeadingsFooter<TTabs>
        tabHeadingConfigs={tabHeadingConfigs}
        context={TabContext}
        showHorizontalRule={showHorizontalRule}
      />
    ),
    TabContentSections: ({
      tabContentConfigs
    }: {
      readonly tabContentConfigs: Array<TabContentConfig<TTabs>>
    }) => (
      <TabContentSections<TTabs>
        context={TabContext}
        tabContentConfigs={tabContentConfigs}
      />
    )
  }
}
